import { Box, Stack } from '@mui/material';
import { compact, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { Card } from '@/components/layout/Card/Card';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { Loader } from '@/components/progress/Loader/Loader';
import { InteractionParadigmContext } from '@/contexts/InteractionParadigm.context';
import { useModalState } from '@/hooks/useModalState';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useAIDPSuggestionsEnabled } from '@/modules/aiDP/hooks/useAIDPSuggestionsEnabled';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  getInitialSectionOptions,
  getSectionForEntityDetailsActiveTab,
  getSectionHasUnacknowledgedSuggestions,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EditEntitySplitScreenOpenModalButton } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenOpenModalButton';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import {
  NonTrustCharitableEntityDetailsTabs,
  PersonalAccountDetailsTabs,
  TrustDetailsTabs,
} from '../details/entityDetails.types';
import {
  EntityDetails,
  EntityDetailsView,
  EntityDetailTab,
} from './EntityDetails';
import { useOpenEntityModalButtonProps } from './EntityDetailsCard.hooks';

export const ENTITY_DETAILS_DEFAULT_FILTER_SEARCH_PARAM = 'entityTab';

export interface EntityDetailsCardProps {
  tabs: EntityDetailTab[];
  loading?: boolean;
  entityId: string;
  householdId: string;
}

export function EntityDetailsCard({
  tabs,
  loading = false,
}: EntityDetailsCardProps) {
  const canEditHouseholdData = useCanEditHouseholdData();

  // Which tab reported that it wants the Edit Entity Details button shown.
  // Currently, this is only applicable for Rev/Irrev Trusts, on the Summary,
  // Trustees, and Beneficiary tabs. If there are no Trustees/Beneficiaries,
  // the button will not be shown (for that tab). We need to pass this as a function
  // that will be set by the child component that actual renders the active tab
  // details, because we don't resolve the entity details until then.
  //
  // We store the reporting tab rather than a bare boolean so the answer can't
  // leak across tabs. Tabs that never report (e.g. Dispositions, which renders
  // its own edit button) would otherwise inherit a stale `true` from whichever
  // tab was viewed previously and render a second, duplicate button. Storing the
  // tab also avoids a reset-on-tab-change effect, which would be clobbered by
  // the child's own effect -- React flushes child effects before parent ones.
  const [tabWithEditEntityButton, setTabWithEditEntityButton] = useState<
    EntityDetailsView | '' | null
  >(null);

  const { activeTab, setActiveTab } = useActiveTab<EntityDetailsView | ''>({
    // Render the default tab without a navigation that can replace a sidebar click.
    defaultTab: tabs[0]?.value ?? '',
    filterSearchParam: ENTITY_DETAILS_DEFAULT_FILTER_SEARCH_PARAM,
  });

  // Only honor the flag for the tab that actually reported it.
  const shouldShowEditEntityButtonForTab =
    tabWithEditEntityButton === activeTab;

  const { entityType } = useEntityDetailsContext();
  const { suggestionsByKind: dpSuggestionsByKind } =
    useDispositiveProvisionSuggestionsContext();
  const aiDpEnabled = useAIDPSuggestionsEnabled(entityType);

  const buttonGroupOptions = useMemo(() => {
    return compact(
      tabs.map(({ display, value }) => {
        return {
          display,
          value,
        };
      })
    );
  }, [tabs]);

  // If the current active tab is not in the list of tabs, set the active tab to the first tab
  useEffect(() => {
    if (
      tabs.length > 0 &&
      tabs.find((tab) => tab.value === activeTab) === undefined
    ) {
      // If we are trying to link the dispositions tab, and it's not in the list of tabs,
      // we can try to intuit the correct disposions tab
      if (activeTab === TrustDetailsTabs.DISPOSITIONS) {
        const otherPossibleDispositionTabs = [
          NonTrustCharitableEntityDetailsTabs.DISPOSITIONS,
          PersonalAccountDetailsTabs.DISPOSITIONS,
        ];

        const otherDispositionTab = otherPossibleDispositionTabs.find((tab) =>
          tabs.find((t) => t.value === tab)
        );

        if (otherDispositionTab) {
          setActiveTab(otherDispositionTab);
          return;
        }
      }

      setActiveTab(tabs[0]!.value);
    }
  }, [activeTab, loading, setActiveTab, tabs]);

  const openEditEntityModalButtonProps = useOpenEntityModalButtonProps(
    activeTab,
    entityType,
    canEditHouseholdData
  );

  const [{ isModalOpen }, { closeModal }] = useModalState();
  const [initialSectionToReview, setInitialSectionToReview] = useState<
    EditEntitySection | undefined
  >();

  const { sectionsWithSuggestions, sectionOptions } = useMemo(() => {
    if (!activeTab || !entityType) {
      return {
        sectionsWithSuggestions: [],
        sectionOptions: [],
      };
    }

    const entitySectionsForTab = getSectionForEntityDetailsActiveTab(
      activeTab,
      entityType
    );

    const mergedSuggestionsByKind = {
      ...(aiDpEnabled ? dpSuggestionsByKind : {}),
    };

    const sectionsWithSuggestions = entitySectionsForTab.filter((section) => {
      return getSectionHasUnacknowledgedSuggestions(
        entityType,
        section,
        mergedSuggestionsByKind
      );
    });

    return {
      sectionsWithSuggestions,
      sectionOptions: getInitialSectionOptions(activeTab, entityType),
    };
  }, [activeTab, aiDpEnabled, dpSuggestionsByKind, entityType]);

  useEffect(() => {
    if (
      !activeTab ||
      isEmpty(sectionsWithSuggestions) ||
      isEmpty(sectionOptions)
    )
      return;

    // When switching entity card tabs, determine what the initial section of
    // the modal should be when the user clicks "review suggestions".
    const section = sectionOptions?.find((section) =>
      sectionsWithSuggestions.includes(section)
    );
    setInitialSectionToReview(section);
  }, [activeTab, sectionOptions, sectionsWithSuggestions]);

  const handleShowEditEntity = useCallback(
    (shouldShowEditButton: boolean) => {
      setTabWithEditEntityButton(shouldShowEditButton ? activeTab : null);
    },
    [activeTab]
  );

  return (
    <InteractionParadigmContext.Provider value={{ viewOnly: false }}>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          pb: 4,
          height: '100%',
        }}
      >
        {loading && (
          <Loader
            boxProps={{
              sx: {
                textAlign: 'center',
                my: 3,
              },
            }}
          />
        )}
        <Box display={loading ? 'none' : 'block'}>
          <Box mb={2}>
            <ButtonGroup<EntityDetailsView | ''>
              label=""
              options={buttonGroupOptions}
              value={activeTab}
              onChange={(_e, value) => {
                // No need to clear the edit-button flag here: it is keyed by the
                // tab that reported it, so it stops applying as soon as the
                // active tab changes.
                setActiveTab(value);
              }}
            />
          </Box>
          {activeTab !== '' && (
            <Stack spacing={2}>
              <EntityDetails
                tabs={tabs}
                activeEntityView={activeTab}
                onShowEditEntity={handleShowEditEntity}
              />
              {openEditEntityModalButtonProps &&
                shouldShowEditEntityButtonForTab && (
                  <Box>
                    <EditEntitySplitScreenOpenModalButton
                      {...openEditEntityModalButtonProps}
                    />
                  </Box>
                )}
              {isModalOpen && initialSectionToReview && entityType && (
                <EditEntitySplitScreenModal
                  initialSection={initialSectionToReview}
                  entityType={entityType}
                  isOpen={isModalOpen}
                  onClose={closeModal}
                />
              )}
            </Stack>
          )}
        </Box>
      </Card>
    </InteractionParadigmContext.Provider>
  );
}
